if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      _isFetchingVariant = false;
      _pendingSubmit = false;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.requiresSizeSelection = this.dataset.requireSizeSelection === 'true';
        this.sizeSelected = this.dataset.sizeSelected !== 'false';

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.setSizeSelectionState(this.sizeSelected, { updateButton: !this.sizeSelected });
        this.initStickyCart();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        window.removeEventListener('scroll', this.handleScrollBound);
        this.stickyObserver?.disconnect();
      }

      initStickyCart() {
        this.stickyContainer = this.querySelector('.product__sticky-cart');
        if (!this.stickyContainer) return;

        const mainSubmit = this.productForm?.submitButton || this.querySelector('.product-form__submit');
        if ('IntersectionObserver' in window && mainSubmit) {
          this.stickyObserver = new IntersectionObserver(
            ([entry]) => {
              const mainButtonIsUsable = entry.isIntersecting && entry.intersectionRatio >= 0.6;
              this.stickyContainer.classList.toggle('sticky-cart--visible', !mainButtonIsUsable);
            },
            { threshold: [0, 0.6] }
          );
          this.stickyObserver.observe(mainSubmit);
          return;
        }

        this.handleScrollBound = this.handleScroll.bind(this);
        window.addEventListener('scroll', this.handleScrollBound, { passive: true });
        this.handleScroll();
      }

      handleScroll() {
        if (!this.stickyContainer) return;
        if (window.scrollY > 400) {
          this.stickyContainer.classList.add('sticky-cart--visible');
        } else {
          this.stickyContainer.classList.remove('sticky-cart--visible');
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        
        const variantSelects = event.target.closest('variant-selects');
        const selectedOptions = variantSelects ? variantSelects.selectedOptions : []; 

        if (!target) return;

        if (this.requiresSizeSelection && event.target.closest('[data-size-picker]')) {
          this.setSizeSelectionState(true, { updateButton: false });
        }

        // Resolve variant ID client-side immediately
        let clientResolvedVariantId = null;
          const variantDataEl = this.querySelector('[id^="VariantData-"]');
        if (variantDataEl) {
          const variantData = JSON.parse(variantDataEl.innerHTML || '[]');
          const clientResolvedVariant = variantData.find((v) => {
            return v.options.every((opt) => selectedOptions.includes(opt));
          });

          if (clientResolvedVariant) {
            clientResolvedVariantId = clientResolvedVariant.id;
            this._confirmedVariantId = clientResolvedVariant.id; // Track the latest client-side resolution
            this.updateVariantInputs(clientResolvedVariant.id);
            this.updateURL(this.dataset.url, clientResolvedVariant.id);
          }
        }

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage,
            clientResolvedVariantId
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, clientResolvedVariantId),
        });

        this._isFetchingVariant = true;
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true, window.variantStrings.selecting || 'Seçiliyor...');
        productForm?.handleErrorMessage();
        this.setVariantSelectionLoading(true);
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
              this.pendingRequestUrl = null;
              this._isFetchingVariant = false;
              this._pendingSubmit = false;
              this.setVariantSelectionLoading(false);
              this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
              this.productForm?.handleErrorMessage(
                'Ürün seçenekleri güncellenemedi. Bağlantınızı kontrol edip seçiminizi tekrar yapın.'
              );
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode?.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false, variantId = null) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues?.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        if (variantId) {
          params.push(`variant=${variantId}`);
        }

        return `${url}${url.includes('?') ? '&' : '?'}${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl, clientResolvedVariantId) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this._isFetchingVariant = false;
            this._pendingSubmit = false;
            this.setVariantSelectionLoading(false);
            this.setUnavailable();
            return;
          }

          if (this.requiresSizeSelection) this.setSizeSelectionState(true, { updateButton: false });

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('StickyPrice');
          updateSourceFromDestination('StickyATC');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          this._isFetchingVariant = false;
          this.setVariantSelectionLoading(false);
          const newVariantId = variant ? variant.id : clientResolvedVariantId;
          if (newVariantId) {
            this._confirmedVariantId = newVariantId;
            this.updateVariantInputs(newVariantId);
          }

          if (this._pendingSubmit) {
            this._pendingSubmit = false;
            this.submitForm();
          }
        };
      }

      submitForm() {
        const productForm = this.productForm;
        if (!productForm) return;

        if (this.requiresSizeSelection && !this.sizeSelected) {
          this.promptSizeSelection();
          return;
        }

        if (this._isFetchingVariant) {
          this._pendingSubmit = true;
          return;
        }

        // Final sync check before submission
        if (this._confirmedVariantId) {
          this.updateVariantInputs(this._confirmedVariantId);
        }

        const submitBtn = productForm.querySelector('[name="add"]');
        if (submitBtn) submitBtn.click();
      }

      updateVariantInputs(variantId) {
        if (!variantId) return;
        
        // Target all product forms within this component
        const forms = this.querySelectorAll('product-form, form[data-type="add-to-cart-form"]');
        forms.forEach(form => {
          const input = form.querySelector('input[name="id"]');
          if (input) {
            input.value = variantId;
            input.removeAttribute('disabled');
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });

        // Fallback: search all inputs named "id" inside this component
        this.querySelectorAll('input[name="id"]').forEach((input) => {
          if (input.value !== variantId.toString()) {
            input.value = variantId;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      setVariantSelectionLoading(isLoading) {
        this.toggleAttribute('aria-busy', isLoading);
        this.classList.toggle('is-variant-selecting', isLoading);
        this.variantSelectors?.toggleAttribute('aria-busy', isLoading);
      }

      setSizeSelectionState(selected, { updateButton = true } = {}) {
        if (!this.requiresSizeSelection) return;

        this.sizeSelected = Boolean(selected);
        this.dataset.sizeSelected = this.sizeSelected ? 'true' : 'false';
        this.classList.toggle('is-size-unselected', !this.sizeSelected);

        const prompt = this.querySelector('.product-form__size-prompt');
        if (prompt) {
          prompt.hidden = this.sizeSelected;
          prompt.textContent = this.sizeSelected ? '' : 'Bedeninizi seçerek devam edin.';
        }

        if (!this.sizeSelected) {
          this.querySelectorAll('input[name="id"]').forEach((input) => {
            input.value = '';
            input.disabled = true;
          });
          if (updateButton) this.productForm?.toggleSubmitButton?.(true, 'Beden Seçin');
        } else {
          this.querySelectorAll('input[name="id"]').forEach((input) => input.removeAttribute('disabled'));
        }

        const stickyButton = this.querySelector('.product__sticky-cart-button');
        if (stickyButton) {
          stickyButton.setAttribute('aria-label', this.sizeSelected ? 'Sepete Ekle' : 'Beden seçiciye git');
          stickyButton.toggleAttribute('data-needs-size-selection', !this.sizeSelected);
          if (!this.sizeSelected) {
            stickyButton.disabled = false;
            stickyButton.setAttribute('aria-disabled', 'false');
          }
        }
      }

      promptSizeSelection() {
        this.setSizeSelectionState(false);
        const picker = this.querySelector('[data-size-picker]');
        if (!picker) return;

        picker.classList.add('size-selection-attention');
        picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const control = picker.matches('select') ? picker : picker.querySelector('input:not(.disabled):not(:disabled), select');
        window.setTimeout(() => {
          control?.focus({ preventScroll: true });
          picker.classList.remove('size-selection-attention');
        }, 450);
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        const loadingSpinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        if (loadingSpinner) loadingSpinner.classList.remove('hidden');

        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => {
            if (loadingSpinner) loadingSpinner.classList.add('hidden');
          });
      }

      getSectionInnerHTML(html, selector) {
        const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
        return element ? element.innerHTML : '';
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return; // Null check for the updated form

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
